/**
 * Sophyteca - Biblioteca Digital Integrada
 * Página principal de busca e navegação
 * Data: 01 de Novembro de 2025
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

interface Livro {
  id: number | null;
  titulo: string;
  autor: string;
  editora?: string;
  ano_publicacao?: number;
  categoria?: string;
  descricao?: string;
  tipo_fonte: 'proprio' | 'externo';
  url_externa?: string;
  url_capa?: string;
  arquivo_url?: string;
  biblioteca_origem?: string;
  total_downloads?: number;
}

interface ResultadoBusca {
  proprios: Livro[];
  externos: Livro[];
  total: number;
}

// Bibliotecas disponíveis
const BIBLIOTECAS_DISPONIVEIS = [
  'Open Library',
  'Google Books',
  'SciELO Books',
  'BN Digital',
  'Livros Abertos USP',
  'Obras Raras USP',
  'BBM USP',
  'OASIS Brasil',
  'Editora FGV',
  'Editora UFSC',
  'Porto Livre Fiocruz',
  'Amazon Gratuitos',
  'Amazon Livros',
  'eBooks Brasil',
  'Domínio Público'
];

export default function Sophyteca() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [resultados, setResultados] = useState<ResultadoBusca | null>(null);
  const [loading, setLoading] = useState(false);
  const [categoria, setCategoria] = useState('');
  const [tipoFonte, setTipoFonte] = useState('');
  const [buscarExternos, setBuscarExternos] = useState(true);
  const [bibliotecasSelecionadas, setBibliotecasSelecionadas] = useState<string[]>([]);

  const handleBuscar = async () => {
    if (!query.trim()) {
      alert('Digite algo para buscar');
      return;
    }

    setLoading(true);
    try {
      console.log('[SOPHYTECA] Buscando:', { query, categoria, tipoFonte, buscarExternos, bibliotecasSelecionadas });

      const params: any = { 
        q: query, 
        categoria: categoria || undefined, 
        tipoFonte: tipoFonte || undefined,
        buscarExternos: buscarExternos.toString()
      };

      // Adicionar bibliotecas selecionadas como string separada por vírgula
      if (bibliotecasSelecionadas.length > 0) {
        params.bibliotecas = bibliotecasSelecionadas.join(',');
      }

      const response = await api.get('/sophyteca/buscar', { params });

      if (response.data.sucesso) {
        setResultados(response.data.dados);
      } else {
        alert('Erro na busca: ' + response.data.mensagem);
      }
    } catch (error: any) {
      console.error('[SOPHYTECA] Erro busca:', error);
      alert('Erro ao buscar livros: ' + (error.response?.data?.mensagem || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (livro: Livro) => {
    try {
      if (livro.tipo_fonte === 'externo' && livro.url_externa) {
        // Registrar acesso
        await api.post('/sophyteca/registrar-acesso', {
          livroId: null,
          acao: 'redirecionamento',
          dadosExtras: { 
            urlExterna: livro.url_externa, 
            biblioteca: livro.biblioteca_origem 
          }
        });

        // Redirecionar para site externo
        window.open(livro.url_externa, '_blank');
      } else if (livro.id) {
        // Download do acervo próprio
        const response = await api.get(`/sophyteca/download/${livro.id}`);
        
        if (response.data.sucesso) {
          window.open(response.data.dados.url, '_blank');
        } else {
          alert('Erro ao baixar: ' + response.data.mensagem);
        }
      }
    } catch (error: any) {
      console.error('[SOPHYTECA] Erro download:', error);
      alert('Erro ao acessar livro: ' + (error.response?.data?.mensagem || error.message));
    }
  };

  const handleAdicionarFavorito = async (livroId: number) => {
    try {
      const response = await api.post('/sophyteca/favoritos', { livroId });
      
      if (response.data.sucesso) {
        alert('Livro adicionado aos favoritos!');
      }
    } catch (error: any) {
      console.error('[SOPHYTECA] Erro favoritar:', error);
      alert('Erro ao favoritar: ' + (error.response?.data?.mensagem || error.message));
    }
  };

  const renderLivroCard = (livro: Livro, index: number) => (
    <div key={livro.id || `ext-${index}`} className="col-md-3 col-sm-6 mb-4">
      <div className="card h-100 shadow-sm">
        {livro.url_capa ? (
          <img 
            src={livro.url_capa} 
            className="card-img-top" 
            alt={livro.titulo}
            style={{ height: '200px', objectFit: 'cover' }}
            onError={(e) => {
              (e.target as HTMLImageElement).src = '/img/book-placeholder.png';
            }}
          />
        ) : (
          <div 
            className="card-img-top d-flex align-items-center justify-content-center bg-light"
            style={{ height: '200px' }}
          >
            <i className="fas fa-book fa-4x text-muted"></i>
          </div>
        )}
        
        <div className="card-body">
          <h6 className="card-title text-truncate" title={livro.titulo}>
            {livro.titulo}
          </h6>
          <p className="card-text text-muted small mb-1">
            <i className="fas fa-user"></i> {livro.autor}
          </p>
          
          {livro.ano_publicacao && (
            <p className="card-text text-muted small mb-1">
              <i className="fas fa-calendar"></i> {livro.ano_publicacao}
            </p>
          )}
          
          {livro.editora && (
            <p className="card-text text-muted small mb-2">
              <i className="fas fa-building"></i> {livro.editora}
            </p>
          )}
          
          {livro.biblioteca_origem && (
            <span className="badge bg-info mb-2">
              {livro.biblioteca_origem}
            </span>
          )}
          
          {livro.categoria && (
            <span className="badge bg-secondary mb-2 ms-1">
              {livro.categoria}
            </span>
          )}

          {livro.descricao && (
            <p className="card-text small text-muted" style={{ fontSize: '0.85rem' }}>
              {livro.descricao.substring(0, 100)}...
            </p>
          )}
        </div>
        
        <div className="card-footer bg-white border-top-0">
          <div className="d-grid gap-2">
            {livro.tipo_fonte === 'proprio' ? (
              <>
                <button
                  className="btn btn-success btn-sm"
                  onClick={() => handleDownload(livro)}
                >
                  <i className="fas fa-download"></i> Baixar
                </button>
                {livro.id && (
                  <button
                    className="btn btn-outline-warning btn-sm"
                    onClick={() => handleAdicionarFavorito(livro.id!)}
                  >
                    <i className="fas fa-star"></i> Favoritar
                  </button>
                )}
              </>
            ) : (
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={() => handleDownload(livro)}
              >
                <i className="fas fa-external-link-alt"></i> Ir para site
              </button>
            )}
          </div>
          
          {livro.total_downloads !== undefined && livro.total_downloads > 0 && (
            <small className="text-muted mt-2 d-block text-center">
              <i className="fas fa-download"></i> {livro.total_downloads} downloads
            </small>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="container-fluid">
      <div className="row mb-4">
        <div className="col-md-8">
          <h2 className="mb-2">
            <i className="fas fa-book-reader mr-2"></i>
            Sophyteca - Biblioteca Digital
          </h2>
          <p className="text-muted">
            Acesse livros e recursos educativos de Psicologia
          </p>
        </div>
        <div className="col-md-4 text-right">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => navigate('/dashboard')}
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Voltar ao Dashboard
          </button>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          {/* Busca */}
          <div className="card shadow">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <input
                    type="text"
                    className="form-control form-control-lg"
                    placeholder="Buscar por título, autor ou palavra-chave..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleBuscar()}
                  />
                </div>
                
                <div className="col-md-3">
                  <select 
                    className="form-select form-select-lg" 
                    value={categoria} 
                    onChange={(e) => setCategoria(e.target.value)}
                  >
                    <option value="">Todas as categorias</option>
                    <option value="psicologia">Psicologia</option>
                    <option value="neurociencia">Neurociência</option>
                    <option value="filosofia">Filosofia</option>
                    <option value="saude_mental">Saúde Mental</option>
                    <option value="educacao">Educação</option>
                    <option value="outros">Outros</option>
                  </select>
                </div>
                
                <div className="col-md-3">
                  <button 
                    className="btn btn-primary btn-lg w-100" 
                    onClick={handleBuscar} 
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Buscando...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-search me-2"></i>
                        Buscar
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="mt-3">
                <div className="form-check form-check-inline">
                  <input
                    type="radio"
                    className="form-check-input"
                    id="todos"
                    checked={tipoFonte === ''}
                    onChange={() => setTipoFonte('')}
                  />
                  <label className="form-check-label" htmlFor="todos">
                    <i className="fas fa-globe"></i> Todos
                  </label>
                </div>
                
                <div className="form-check form-check-inline">
                  <input
                    type="radio"
                    className="form-check-input"
                    id="proprio"
                    checked={tipoFonte === 'proprio'}
                    onChange={() => {
                      setTipoFonte('proprio');
                      setBuscarExternos(false);
                    }}
                  />
                  <label className="form-check-label" htmlFor="proprio">
                    <i className="fas fa-book"></i> Acervo Sophya
                  </label>
                </div>
                
                <div className="form-check form-check-inline">
                  <input
                    type="radio"
                    className="form-check-input"
                    id="externo"
                    checked={tipoFonte === 'externo'}
                    onChange={() => {
                      setTipoFonte('externo');
                      setBuscarExternos(true);
                    }}
                  />
                  <label className="form-check-label" htmlFor="externo">
                    <i className="fas fa-university"></i> Bibliotecas Públicas
                  </label>
                </div>

                <div className="form-check form-check-inline ms-4">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="buscarExternos"
                    checked={buscarExternos}
                    onChange={(e) => setBuscarExternos(e.target.checked)}
                    disabled={tipoFonte === 'proprio'}
                  />
                  <label className="form-check-label" htmlFor="buscarExternos">
                    Incluir bibliotecas externas
                  </label>
                </div>
              </div>

              {/* Filtro de Bibliotecas (multi-seleção) */}
              {(buscarExternos || tipoFonte === 'externo') && (
                <div className="mt-3 p-3 border rounded bg-light">
                  <h6 className="mb-3">
                    <i className="fas fa-filter mr-2"></i>
                    Selecionar Bibliotecas Específicas (opcional)
                  </h6>
                  <div className="row">
                    {BIBLIOTECAS_DISPONIVEIS.map((biblioteca) => (
                      <div key={biblioteca} className="col-md-3 col-sm-6 mb-2">
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id={`bib-${biblioteca}`}
                            checked={bibliotecasSelecionadas.includes(biblioteca)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setBibliotecasSelecionadas([...bibliotecasSelecionadas, biblioteca]);
                              } else {
                                setBibliotecasSelecionadas(bibliotecasSelecionadas.filter(b => b !== biblioteca));
                              }
                            }}
                          />
                          <label className="form-check-label" htmlFor={`bib-${biblioteca}`}>
                            <small>{biblioteca}</small>
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary me-2"
                      onClick={() => setBibliotecasSelecionadas(BIBLIOTECAS_DISPONIVEIS)}
                    >
                      <i className="fas fa-check-square mr-1"></i>
                      Selecionar Todas
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setBibliotecasSelecionadas([])}
                    >
                      <i className="fas fa-square mr-1"></i>
                      Limpar Seleção
                    </button>
                    <small className="text-muted ms-3">
                      {bibliotecasSelecionadas.length > 0 
                        ? `${bibliotecasSelecionadas.length} ${bibliotecasSelecionadas.length === 1 ? 'biblioteca selecionada' : 'bibliotecas selecionadas'}`
                        : 'Nenhuma selecionada (buscar em todas)'}
                    </small>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Resultados */}
          {resultados && (
            <div className="card mt-4 shadow">
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0">
                  <i className="fas fa-search"></i> Resultados da Busca ({resultados.total})
                </h5>
              </div>
              
              <div className="card-body">
                {/* Acervo próprio */}
                {resultados.proprios.length > 0 && (
                  <>
                    <h6 className="text-primary mb-3">
                      <i className="fas fa-book"></i> Acervo Sophya ({resultados.proprios.length})
                    </h6>
                    <div className="row">
                      {resultados.proprios.map((livro, index) => renderLivroCard(livro, index))}
                    </div>
                  </>
                )}

                {/* Bibliotecas externas */}
                {resultados.externos.length > 0 && (
                  <>
                    {resultados.proprios.length > 0 && <hr className="my-4" />}
                    
                    <h6 className="text-info mb-3">
                      <i className="fas fa-globe"></i> Bibliotecas Públicas ({resultados.externos.length})
                    </h6>
                    <div className="row">
                      {resultados.externos.map((livro, index) => renderLivroCard(livro, index))}
                    </div>
                  </>
                )}

                {/* Nenhum resultado */}
                {resultados.total === 0 && (
                  <div className="text-center py-5">
                    <i className="fas fa-search fa-4x text-muted mb-3"></i>
                    <h5 className="text-muted">Nenhum resultado encontrado</h5>
                    <p className="text-muted">
                      Tente usar palavras-chave diferentes ou verifique a ortografia
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Botões de Ação */}
          <div className="mt-4">
            <button 
              className="btn btn-outline-secondary me-2"
              onClick={() => navigate('/sophyteca/favoritos')}
            >
              <i className="fas fa-star"></i> Meus Favoritos
            </button>
            
            <button 
              className="btn btn-outline-primary"
              onClick={() => navigate('/sophyteca/admin')}
            >
              <i className="fas fa-upload"></i> Adicionar Livro
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
